/**
 * Global offline banner — shows a thin bar when the device loses connectivity.
 * Useful for Waylo's bus-riding audience on patchy mobile data. TanStack Query
 * already refetches on reconnect (Phase F); this is the visible signal.
 */
import { useEffect, useState } from 'react';
import { useT } from '../lib/i18n';

export function OfflineBanner(): JSX.Element | null {
  const { t } = useT();
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);

  useEffect(() => {
    const goOnline = (): void => setOffline(false);
    const goOffline = (): void => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      className="fixed left-0 right-0 top-0 z-50 bg-[var(--warning)] px-4 py-2 text-center text-xs font-semibold text-white"
      style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0))' }}
    >
      {t('common.offline')}
    </div>
  );
}
